import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, DollarSign, XCircle } from 'lucide-react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { DatePicker } from '@/shared/components/DatePicker';
import { Field } from '@/shared/components/Field';
import { Input } from '@/shared/components/Input';
import { MoneyInput } from '@/shared/components/MoneyInput';
import { Select } from '@/shared/components/Select';
import { Chip } from '@/shared/components/Chip';
import { StepUpAuthModal } from '@/shared/components/StepUpAuthModal';
import { useToast } from '@/shared/components/Toast';
import { useSession } from '@/core/auth/useSession';
import { AppError } from '@/core/errors/AppError';
import { formatearFecha, formatearHora, formatearMonto } from '@/core/utils/formatters';
import { useAplicarPrecioOverride, useAsignarEmpleadoClase, useCambiarEmpleadoClase, useCancelarClase, useDesasignarClase, useEditarClase, useMarcarDictada, } from '@/data/hooks/useClases';
import { useEmpleados } from '@/data/hooks/useCatalogos';
import { useDisciplinas } from '@/data/hooks/useEmpleados';
const EditarSchema = z.object({
    fechaHoraInicio: z.string().min(1, 'Seleccioná fecha y hora'),
    duracionHoras: z
        .string()
        .regex(/^\d{1,2}(\.\d{1,2})?$/, 'Duración inválida')
        .or(z.literal('')),
    empleadoId: z.string().uuid().optional().or(z.literal('')),
    disciplinaClave: z.string().optional().or(z.literal('')),
});
function toInputLocal(iso) {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function EditarClaseModal({ open, onOpenChange, clase }) {
    const toast = useToast();
    const { tieneRol } = useSession();
    const { data: empleados } = useEmpleados();
    const { data: disciplinas } = useDisciplinas();
    const editar = useEditarClase();
    const asignar = useAsignarEmpleadoClase();
    const cambiar = useCambiarEmpleadoClase();
    const desasignar = useDesasignarClase();
    const dictada = useMarcarDictada();
    const cancelar = useCancelarClase();
    const override = useAplicarPrecioOverride();
    const [stepUpOpen, setStepUpOpen] = useState(false);
    const [overrideMonto, setOverrideMonto] = useState('');
    const puedeOverride = tieneRol('ROLE_OVERRIDE_PRECIO_CLASE');
    const form = useForm({
        resolver: zodResolver(EditarSchema),
        defaultValues: { fechaHoraInicio: '', duracionHoras: '', empleadoId: '' },
    });
    useEffect(() => {
        if (!clase || !open)
            return;
        form.reset({
            fechaHoraInicio: toInputLocal(clase.fechaHoraInicio),
            duracionHoras: String(clase.duracionHoras),
            empleadoId: clase.empleadoId ?? '',
            disciplinaClave: clase.disciplina ?? '',
        });
    }, [clase, open, form]);
    if (!clase)
        return null;
    const bloqueada = clase.estado === 'DICTADA' || clase.estado === 'CANCELADA';
    const empleadoOriginal = clase.empleadoId ?? '';
    const errors = form.formState.errors;
    const onSubmit = form.handleSubmit(async (values) => {
        try {
            const nuevoISO = new Date(values.fechaHoraInicio).toISOString();
            const claveOriginal = clase.disciplina ?? '';
            const claveNueva = values.disciplinaClave ?? '';
            const cambioDisciplina = claveNueva !== claveOriginal;
            const cambioHorario = nuevoISO !== clase.fechaHoraInicio ||
                (values.duracionHoras && Number(values.duracionHoras) !== clase.duracionHoras);
            if (cambioHorario || cambioDisciplina) {
                const disciplinaId = cambioDisciplina
                    ? claveNueva
                        ? disciplinas?.find((d) => d.clave === claveNueva)?.id ?? null
                        : 0
                    : null;
                await editar.mutateAsync({
                    id: clase.id,
                    datos: {
                        fechaHoraInicio: cambioHorario ? nuevoISO : undefined,
                        duracionHoras: cambioHorario ? values.duracionHoras || undefined : undefined,
                        disciplinaId: disciplinaId ?? undefined,
                    },
                });
            }
            if (values.empleadoId !== empleadoOriginal) {
                if (!values.empleadoId) {
                    // Pasa a "sin asignar": cerramos el tramo en backend.
                    await desasignar.mutateAsync({ id: clase.id });
                }
                else if (empleadoOriginal === '') {
                    await asignar.mutateAsync({ id: clase.id, empleadoId: values.empleadoId });
                }
                else {
                    await cambiar.mutateAsync({ id: clase.id, empleadoId: values.empleadoId });
                }
            }
            toast.exito('Clase actualizada');
            onOpenChange(false);
        }
        catch (err) {
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar los cambios.');
        }
    });
    const accionDictada = () => {
        dictada.mutate(clase.id, {
            onSuccess: () => {
                toast.exito('Clase marcada como dictada');
                onOpenChange(false);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos marcarla.'),
        });
    };
    const accionCancelar = () => {
        cancelar.mutate({ id: clase.id }, {
            onSuccess: () => {
                toast.exito('Clase cancelada');
                onOpenChange(false);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos cancelar la clase.'),
        });
    };
    return (_jsxs(Modal, { open: open, onOpenChange: onOpenChange, children: [_jsxs(ModalContent, { size: "md", children: [_jsx(ModalHeader, { titulo: "Clase", descripcion: `${formatearFecha(clase.fechaHoraInicio)} · ${formatearHora(clase.fechaHoraInicio)}` }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsxs(ModalBody, { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Chip, { tono: "primary", children: clase.tipoClase }), clase.disciplina ? _jsx(Chip, { tono: "info", children: clase.disciplina }) : null, _jsx(Chip, { tono: clase.estado === 'DICTADA'
                                                    ? 'success'
                                                    : clase.estado === 'CANCELADA'
                                                        ? 'danger'
                                                        : clase.estado === 'ASIGNADA'
                                                            ? 'warning'
                                                            : 'neutral', children: clase.estado })] }), _jsxs("div", { className: "grid grid-cols-1 gap-4 sm:grid-cols-2", children: [_jsx(Field, { label: "Fecha y hora", htmlFor: "fechaHoraInicio", error: errors.fechaHoraInicio?.message, required: true, children: _jsx(DatePicker, { id: "fechaHoraInicio", modo: "datetime", disabled: bloqueada, value: form.watch('fechaHoraInicio') ?? '', onChange: (v) => form.setValue('fechaHoraInicio', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "Duraci\u00F3n (h)", htmlFor: "duracionHoras", error: errors.duracionHoras?.message, children: _jsx(Input, { id: "duracionHoras", inputMode: "decimal", disabled: bloqueada, ...form.register('duracionHoras') }) }), _jsx(Field, { label: "Disciplina", htmlFor: "disciplinaClave", helper: "Cambiar de disciplina no recalcula el monto: las tarifas son por personas y horas.", children: _jsxs(Select, { id: "disciplinaClave", disabled: bloqueada, ...form.register('disciplinaClave'), children: [_jsx("option", { value: "", children: "Sin disciplina" }), disciplinas?.map((d) => (_jsx("option", { value: d.clave, children: d.nombre ?? d.clave }, d.clave)))] }) }), _jsx(Field, { label: "Instructor", htmlFor: "empleadoId", error: errors.empleadoId?.message, className: "sm:col-span-2", children: _jsxs(Select, { id: "empleadoId", disabled: bloqueada, ...form.register('empleadoId'), children: [_jsx("option", { value: "", children: "Sin asignar" }), empleados?.map((e) => (_jsxs("option", { value: e.id, children: [e.nombre, " ", e.apellido] }, e.id)))] }) })] }), puedeOverride && !bloqueada ? (_jsxs("div", { className: "rounded-md border border-warning/40 bg-warning-fill/40 p-3 text-xs", children: [_jsxs("div", { className: "mb-2 flex items-center gap-1.5 font-semibold text-text-primary", children: [_jsx(DollarSign, { className: "h-3.5 w-3.5" }), "Override de precio"] }), clase.precioOverride != null ? (_jsxs("div", { className: "mb-2 text-text-secondary", children: ["Override vigente:", ' ', _jsx("span", { className: "num font-semibold", children: formatearMonto(Number(clase.precioOverride)) }), clase.precioOverrideMotivo ? (_jsxs(_Fragment, { children: [' ', "\u2014 ", _jsx("em", { children: clase.precioOverrideMotivo })] })) : null] })) : null, _jsxs("div", { className: "flex items-end gap-2", children: [_jsx(Field, { label: "Nuevo precio", className: "flex-1", children: _jsx(MoneyInput, { value: overrideMonto, onChange: setOverrideMonto }) }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", disabled: !overrideMonto || override.isPending, onClick: () => setStepUpOpen(true), children: "Aplicar\u2026" }), clase.precioOverride != null ? (_jsx(Button, { type: "button", size: "sm", variant: "ghost", className: "text-danger", onClick: () => {
                                                            setOverrideMonto('');
                                                            setStepUpOpen(true);
                                                        }, children: "Quitar\u2026" })) : null] })] })) : null] }), _jsxs(ModalFooter, { className: "justify-between", children: [_jsxs("div", { className: "flex items-center gap-2", children: [!bloqueada && clase.empleadoId ? (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", className: "text-success", onClick: accionDictada, cargando: dictada.isPending, children: [_jsx(CheckCircle2, { className: "h-4 w-4" }), "Marcar dictada"] })) : null, !bloqueada ? (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", className: "text-danger", onClick: accionCancelar, cargando: cancelar.isPending, children: [_jsx(XCircle, { className: "h-4 w-4" }), "Cancelar clase"] })) : null] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), children: "Cerrar" }), !bloqueada ? (_jsx(Button, { type: "submit", cargando: editar.isPending ||
                                                    asignar.isPending ||
                                                    cambiar.isPending ||
                                                    desasignar.isPending, children: "Guardar" })) : null] })] })] })] }), _jsx(StepUpAuthModal, { open: stepUpOpen, onOpenChange: setStepUpOpen, proposito: "override_precio_clase", titulo: overrideMonto ? 'Autorizar override de precio' : 'Quitar override de precio', descripcion: "Ingres\u00E1 tu contrase\u00F1a y el motivo \u2014 queda en la auditor\u00EDa.", onConfirmar: async ({ token, motivo }) => {
                    try {
                        await override.mutateAsync({
                            id: clase.id,
                            monto: overrideMonto || null,
                            motivo,
                            stepUpToken: token,
                        });
                        toast.exito(overrideMonto ? 'Override aplicado' : 'Override quitado');
                        setOverrideMonto('');
                    }
                    catch (err) {
                        toast.error(err instanceof AppError ? err.mensaje : 'No pudimos aplicar el override.');
                    }
                } })] }));
}
